import { useEffect, useState } from 'react';
import { useAppStore, roleOf } from '../store/useAppStore.js';
import { useLobby } from '../lobby/LobbyContext.js';
import { useCallQueue } from '../hooks/useCallQueue.js';
import { getQueueAvailability } from '../api/client.js';
import { notificationsSupported, getPermission, requestPermission, shouldShowEnablePrompt } from '../lib/notifications.js';
import { AddMinutesPanel } from './AddMinutesPanel.js';
import { ConnectingModal } from './ConnectingModal.js';
import { DurationSelector, DEFAULT_DURATION_MINUTES } from './DurationSelector.js';
import { TopicSelector } from './TopicSelector.js';
import { resolveTopic } from '../lib/topic.js';
import { CamOnIcon, AlertIcon, HandIcon } from '../icons.js';

const AVAILABILITY_POLL_MS = 10_000;

export function LobbyView() {
  const { participants, readyParticipants, pendingPeer, error, signedInElsewhere, useThisDevice, initiateCall, cancelCall, ready, setReady } = useLobby();
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_DURATION_MINUTES);
  const [topicChoice, setTopicChoice] = useState<string | null>(null);
  const [topicOther, setTopicOther] = useState('');
  const topic = resolveTopic(topicChoice, topicOther);
  const userId = useAppStore((s) => s.userId);
  const users = useAppStore((s) => s.users);
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);
  const setLoopbackOf = useAppStore((s) => s.setLoopbackOf);

  const selfRole = roleOf(users, userId);
  const isLearner = selfRole === 'learner';
  const isGuide = selfRole === 'guide';
  const noMinutes = isLearner && sessionMinutes <= 0;

  // `participants` are uuids, so the roster lookup keys straight off them.
  // A learner only counts guides who are Ready (LiveKit `ready` attribute ===
  // '1'): the queue never offers a Standby guide, so counting one would show a
  // Start button that can never connect. iOS applies the identical rule.
  const lobbyGuidesReady = readyParticipants.filter((p) => roleOf(users, p) === 'guide').length;
  const learnersOnline = participants.filter((p) => roleOf(users, p) === 'learner');

  const queue = useCallQueue({ durationMinutes, topic, initiateCall, cancelCall, pendingPeer, inCall: view === 'call' });

  // A Ready iOS guide whose app is closed is not in the lobby room, but
  // /api/queue/next can ring them by VoIP push. The server's availability
  // count covers that case; polled every 10 s while a learner is idle here.
  // max(), not a sum: a guide in the room is in both counts.
  const [serverGuidesReady, setServerGuidesReady] = useState(0);
  // (LobbyView only renders in the lobby, so no view check is needed.)
  const pollAvailability = isLearner && queue.status === 'idle';
  useEffect(() => {
    if (!pollAvailability) return;
    let cancelled = false;
    const tick = () => {
      getQueueAvailability()
        .then((r) => { if (!cancelled) setServerGuidesReady(r.guidesReady); })
        .catch(() => { /* best-effort; the lobby count still works */ });
    };
    tick();
    const id = setInterval(tick, AVAILABILITY_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollAvailability]);
  const guidesReadyCount = Math.max(lobbyGuidesReady, serverGuidesReady);

  // Kicked by a second device: a learner search still polling /api/queue/next
  // would ring guides for a tab that can no longer talk to them. Stop it and
  // release the slot; the takeover card below replaces the lobby until the
  // user chooses this device.
  const queueStop = queue.stop;
  useEffect(() => {
    if (signedInElsewhere) queueStop();
  }, [signedInElsewhere, queueStop]);

  if (signedInElsewhere) {
    return <SignedInElsewhereCard onUseThisDevice={useThisDevice} />;
  }

  const main = (
    <div className="flex-1 min-w-0 space-y-10 animate-fade-up">
      {/* Self-test section */}
      <section className="card p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-eyebrow mb-2">Self-test</div>
            <h2 className="h-card mb-1 text-ink-900 dark:text-ink-100">
              Check your camera and mic
            </h2>
            <p className="hint max-w-md">
              A quick private preview of what others will see — never visible to anyone else.
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost shrink-0"
            onClick={() => { if (userId) { setLoopbackOf(userId); setView('loopback'); } }}
          >
            <CamOnIcon /> Test myself
          </button>
        </div>
      </section>

      {/* Lobby section */}
      <section>
        <div className="flex items-end justify-between mb-5 gap-3">
          <div>
            <div className="text-eyebrow mb-1">Lobby</div>
            <h2 className="h-section text-ink-900 dark:text-ink-100 flex items-center gap-3">
              {isLearner ? 'Guides online now' : isGuide ? (ready ? "You're ready for calls" : "You're on standby") : 'Online now'}
              {isGuide ? (
                <span
                  aria-label={ready ? 'Ready' : 'On standby'}
                  className={
                    ready
                      ? 'w-2.5 h-2.5 rounded-full bg-status-successBright shadow-[0_0_0_4px_rgba(95,192,140,0.2)] animate-blink'
                      : 'w-2.5 h-2.5 rounded-full bg-ink-300 dark:bg-ink-600'
                  }
                />
              ) : null}
            </h2>
          </div>
          {isLearner ? (
            <span className="text-[0.85rem] text-ink-500 dark:text-ink-400 hidden sm:block">
              {guidesReadyCount} {guidesReadyCount === 1 ? 'guide' : 'guides'} ready
            </span>
          ) : null}
        </div>

        {isLearner ? (
          <p className="hint mb-5">Start a session and we'll connect you with the next available guide.</p>
        ) : isGuide ? (
          <p className="hint mb-5">
            {ready
              ? "You'll receive the next call in the queue. Go on standby to pause."
              : "You're online but not receiving calls yet. Click below when you're ready."}
          </p>
        ) : (
          <p className="hint mb-5">People online right now.</p>
        )}

        {isLearner ? (
          <>
            <div className="mb-6">
              <div className="text-eyebrow mb-2">Session length</div>
              <DurationSelector value={durationMinutes} onChange={setDurationMinutes} />
            </div>
            <div className="mb-6">
              <div className="text-eyebrow mb-2">Session topic</div>
              <p className="hint mb-3">Optional — helps your guide prepare.</p>
              <TopicSelector
                choice={topicChoice}
                other={topicOther}
                onChoiceChange={setTopicChoice}
                onOtherChange={setTopicOther}
              />
            </div>
          </>
        ) : null}

        {isGuide ? (
          <>
            <div className="mb-6">
              {ready ? (
                <button type="button" className="btn-ghost" onClick={() => setReady(false)}>
                  Go on Standby
                </button>
              ) : (
                <button type="button" className="btn-primary" onClick={() => setReady(true)}>
                  I&rsquo;m Ready for Calls
                </button>
              )}
            </div>
            <GuideQueueCard count={learnersOnline.length} ready={ready} />
            <EnableNotificationsPrompt />
          </>
        ) : (
          <div className="flex flex-col items-start gap-4">
            <p className="text-[0.95rem] font-medium text-ink-900 dark:text-ink-100">
              {guidesReadyCount > 0
                ? 'Guides are ready to take your call.'
                : 'No guides are online. Please wait for the next available guide.'}
            </p>
            <button
              type="button"
              className="btn-primary"
              disabled={noMinutes || guidesReadyCount === 0 || queue.status !== 'idle'}
              onClick={() => queue.start()}
            >
              Start ASL Go Session
            </button>
            {noMinutes ? <p className="hint">Add minutes to start a session.</p> : null}
            {queue.status === 'waiting' ? (
              <p className="hint">Waiting for the next available guide… <button type="button" className="underline" onClick={() => queue.stop()}>Cancel</button></p>
            ) : null}
          </div>
        )}

        {error ? (
          <div className="notice-error mt-6">
            <AlertIcon className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        ) : null}
      </section>
    </div>
  );

  // While a learner's invite is out to a guide, overlay the connecting popup.
  // Bound to pendingPeer, so accept/decline/timeout dismiss it for free.
  const connectingModal = pendingPeer ? (
    <ConnectingModal onCancel={() => queue.stop()} durationMinutes={durationMinutes} topic={topic} />
  ) : null;

  if (!isLearner) return main;

  // Learner: side-by-side with the wallet/Add-Minutes card on the right.
  return (
    <>
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start animate-fade-up">
        {main}
        <div className="w-full lg:w-[340px] xl:w-[360px] lg:flex-shrink-0">
          <AddMinutesPanel />
        </div>
      </div>
      {connectingModal}
    </>
  );
}

function SignedInElsewhereCard({ onUseThisDevice }: { onUseThisDevice: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] animate-fade-up">
      <section className="card max-w-md w-full p-8 sm:p-10 text-center">
        <div className="text-eyebrow mb-3">Lobby</div>
        <h2 className="h-card text-ink-900 dark:text-ink-100 mb-3">Open on another device</h2>
        <p className="hint mb-7">
          You opened ASL Go somewhere else, so this window left the lobby. ASL Go
          takes calls on one device at a time. To take calls here instead, use this device.
        </p>
        <button type="button" className="btn-primary" onClick={onUseThisDevice}>
          Use this device
        </button>
      </section>
    </div>
  );
}

function EnableNotificationsPrompt() {
  // Notification.permission has no change event, so mirror it in local state and
  // update on the user's decision; the prompt then hides itself immediately.
  const [permission, setPermission] = useState<NotificationPermission>(() => getPermission());
  if (!shouldShowEnablePrompt({ supported: notificationsSupported(), permission })) return null;
  return (
    <div className="mt-5 flex justify-center">
      <button
        type="button"
        className="inline-flex items-center gap-2 text-[0.85rem] font-medium underline text-ink-900 dark:text-brand-bright"
        onClick={async () => { setPermission(await requestPermission()); }}
      >
        Enable notifications so you don&rsquo;t miss a call
      </button>
    </div>
  );
}

function GuideQueueCard({ count, ready }: { count: number; ready: boolean }) {
  // Friendly empty state — no lone "0". Reassures the waiting guide and shows
  // they're discoverable. (Avg-wait / sessions-today stats are intentionally
  // omitted: no client-side data source exists for them yet.)
  const pill = ready ? (
    <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 dark:bg-status-successBright/10 text-emerald-700 dark:text-status-successBright text-[0.8rem] font-medium ring-1 ring-emerald-200 dark:ring-status-successBright/25">
      <span className="w-1.5 h-1.5 rounded-full bg-status-successBright animate-pulse-soft" />
      Ready — you&rsquo;ll get the next call
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-ink-100 dark:bg-white/[0.06] text-ink-500 dark:text-ink-400 text-[0.8rem] font-medium ring-1 ring-ink-200/70 dark:ring-white/10">
      <span className="w-1.5 h-1.5 rounded-full bg-ink-400 dark:bg-ink-500" />
      On standby — not receiving calls
    </span>
  );

  if (count === 0) {
    return (
      <div className="rounded-2xl border border-ink-200/60 dark:border-white/[0.07] bg-surface-light dark:bg-surface-darkTint p-8 sm:p-10 text-center has-grain">
        <div className="w-16 h-16 rounded-full bg-status-successBright/10 flex items-center justify-center mx-auto mb-4">
          <HandIcon width={30} height={30} className="text-status-successBright" />
        </div>
        <div className="text-lg font-semibold text-ink-900 dark:text-ink-100 mb-1.5">
          No learners waiting just yet
        </div>
        <p className="hint max-w-sm mx-auto">
          You'll get a notification the moment someone wants to connect. You can keep
          this tab in the background.
        </p>
        <div className="mt-6 pt-6 border-t border-ink-200/60 dark:border-white/[0.07] flex justify-center">
          {pill}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-8 sm:p-10 text-center has-grain">
      <div className="text-eyebrow mb-3">Available learners</div>
      <div className="font-mono tabular-nums text-7xl font-semibold leading-none text-ink-900 dark:text-ink-100">
        {count}
      </div>
      <p className="mt-3 text-[0.95rem] text-ink-600 dark:text-[#b6b2c4]">
        {count === 1 ? 'learner is online and may call you' : 'learners are online and may call you'}.
      </p>
      <div className="mt-6">{pill}</div>
    </div>
  );
}

